// Responsive data view wrapper component
// CREATED: Conditional wrapper that shows table on desktop and cards on mobile
// Used by: UserManagement, SiteManagement, ServiceManagement, TemplateLibrary
// Updated: Added support for loading states and infinite scroll pagination
// FIXED: Added infinite scroll loader for desktop view (previously only available on mobile)
// UPDATED: Added tableSkeletonComponent prop to display loading state during initial desktop loading
// UPDATED: Replaced skeleton loaders with BouncingDotsLoader for consistent loading UX

import { ReactNode } from 'react';
import { BouncingDotsLoader } from './BouncingDotsLoader';
import { InfiniteScrollLoader } from './InfiniteScrollLoader';

interface ResponsiveDataViewProps<T> {
  items: T[];
  isDesktop: boolean;
  tableComponent: ReactNode;
  cardRenderer: (item: T) => ReactNode;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  skeletonCount?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  tableSkeletonComponent?: ReactNode;
}

export function ResponsiveDataView<T>({
  items,
  isDesktop,
  tableComponent,
  cardRenderer,
  isLoading = false,
  isLoadingMore = false,
  skeletonCount = 5,
  onLoadMore,
  hasMore = false,
  tableSkeletonComponent,
}: ResponsiveDataViewProps<T>) {
  if (isDesktop) {
    // Show skeleton during initial load on desktop
    if (isLoading && items.length === 0 && tableSkeletonComponent) {
      return <div>{tableSkeletonComponent}</div>;
    }

    return (
      <div>
        {tableComponent}
        {/* Infinite scroll loader for desktop */}
        {onLoadMore && (
          <InfiniteScrollLoader
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoading={isLoadingMore}
          />
        )}
      </div>
    );
  }

  // Show loader on initial load
  if (isLoading && items.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <BouncingDotsLoader size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 space-y-3">
        {items.map((item, index) => (
          <div key={index}>
            {cardRenderer(item)}
          </div>
        ))}
      </div>

      {/* Infinite scroll loader */}
      {onLoadMore && (
        <InfiniteScrollLoader
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoadingMore}
        />
      )}
    </div>
  );
}
